import React, { useEffect, useMemo, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { useTranslation } from '../hooks/useTranslation';

const DishQrModal = ({
  open,
  onClose,
  dishId,
  publicShareToken = '',
  dishName = '',
  dishImage = '',
  affiliateName = '',
  affiliateImage = '',
  showPrint = false,
}) => {
  const { t } = useTranslation();
  const printRef = useRef(null);
  const panelRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const consupediaLogoUrl = "/assets/images/consupedia_logo_blue.svg";
  const url = useMemo(() => {
    if (!publicShareToken) return '';
    const origin = window.location.origin;
    return `${origin}/public/items/${publicShareToken}`;
  }, [publicShareToken]);

  useEffect(() => {
    if (!open) return undefined;
    // Ensure modal is visible even if user scrolled far down
    const id = window.setTimeout(() => {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
    return () => window.clearTimeout(id);
  }, [open]);
  useEffect(() => {
    if (!open) setIsPrinting(false);
  }, [open]);

  if (!open) return null;

  const handlePrint = () => {
    if (isPrinting) return;
    if (!printRef.current) return;
    setIsPrinting(true);
    const canvas = printRef.current.querySelector('canvas');
    const qrImageData = canvas ? canvas.toDataURL('image/png') : '';
    const printWindow = window.open('', '_blank', 'width=800,height=900');
    if (!printWindow) {
      setIsPrinting(false);
      return;
    }
    const imageHtml =
      dishImage
        ? `<div class="dishimg"><img src="${dishImage}" alt="${dishName || `Rätt ${dishId}`}" /></div>`
        : '';
    const affiliateHtml = affiliateName
      ? `<div class="affiliate">
          ${affiliateImage ? `<img src="${affiliateImage}" alt="${affiliateName}" />` : ''}
          <span>${affiliateName}</span>
        </div>`
      : '';
    const poweredByHtml = url
      ? `<div class="poweredby">
          <span>Menuwiz - powered by</span>
          <img src="${consupediaLogoUrl}" alt="Consupedia" />
        </div>`
      : '';
    const html = `
      <html>
        <head>
          <title>QR - ${dishName || `Rätt ${dishId}`}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 24px; }
            .wrap { display: flex; flex-direction: column; align-items: center; gap: 16px; }
            .title { font-size: 24px; font-weight: 700; text-align: center; }
            .sub { font-size: 14px; color: #666; text-align: center; }
            .qrbox { border: 1px solid #ddd; border-radius: 16px; padding: 20px; display:flex; flex-direction:column; align-items:center; gap:10px; }
            .affiliate { display: flex; align-items: center; gap: 10px; margin-top: 14px; color: #1f2937; font-size: 16px; font-weight: 700; }
            .affiliate img { width: 34px; height: 34px; border-radius: 999px; object-fit: cover; border: 1px solid #ddd; }
            .poweredby { display: flex; align-items: center; justify-content: center; gap: 10px; color: #374151; font-size: 14px; font-weight: 700; }
            .poweredby img { height: 22px; width: auto; }
            .dishimg { margin-top: 12px; }
            .dishimg img { width: 320px; height: 220px; object-fit: cover; border: 1px solid #ddd; border-radius: 12px; }
          </style>
        </head>
        <body>
          <div class="wrap">
            <div class="title">${dishName || `Rätt ${dishId}`}</div>
            <div class="sub">Skanna för att se rätten</div>
            <div class="qrbox">
              ${qrImageData ? `<img src="${qrImageData}" width="320" height="320" alt="QR-kod" />` : ''}
              ${poweredByHtml}
            </div>
            ${imageHtml}
            ${affiliateHtml}
          </div>
          <script>window.onload = function() { window.print(); window.close(); };</script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
    // Keep controls locked while print dialog is open.
    // Unlock when this window regains focus (dialog closed), with long fallback.
    const releasePrintLock = () => setIsPrinting(false);
    window.addEventListener('focus', releasePrintLock, { once: true });
    window.setTimeout(() => setIsPrinting(false), 30000);
  };

  return (
    <div className="fixed inset-0 z-[1000]">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        role="button"
        tabIndex={-1}
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div ref={panelRef} className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="p-5 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-lg font-extrabold text-gray-900 truncate">
                {t('QR-kod')}
              </div>
              <div className="text-sm text-gray-600 truncate">
                {dishName ? dishName : `Rätt ${dishId}`}
              </div>
            </div>
            <button
              onClick={onClose}
              className="shrink-0 w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-black"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className="px-5 pb-5">
            {!publicShareToken ? (
              <p className="text-sm text-gray-600 text-center py-6 px-2">
                {t('Aktivera <span className="font-semibold text-gray-900">Publik rättsida</span> under Måltidsinställningar för att skapa en delningslänk och QR-kod.')}
              </p>
            ) : null}
            <div
              ref={printRef}
              className="bg-gray-50 border border-gray-100 rounded-2xl p-5 flex flex-col items-center justify-center gap-3"
            >
              {url ? <QRCodeCanvas value={url} size={220} includeMargin /> : null}
              {url ? (
                <div className="flex items-center justify-center gap-2 text-xs text-gray-600 font-bold">
                  <span>Menuwiz - powered by</span>
                  <img
                    src={consupediaLogoUrl}
                    alt="Consupedia"
                    className="h-6 w-auto"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              ) : null}
            </div>
            {showPrint ? (
              <>
                {dishImage ? (
                  <div className="mt-3 border border-gray-100 rounded-2xl overflow-hidden bg-white">
                    <img
                      src={dishImage}
                      alt={dishName || `Rätt ${dishId}`}
                      className="w-full h-40 object-cover"
                    />
                  </div>
                ) : null}
                {affiliateName ? (
                  <div className="mt-3 flex items-center gap-2.5 text-base text-gray-800 font-bold">
                    {affiliateImage ? (
                      <img
                        src={affiliateImage}
                        alt={affiliateName}
                        className="w-8 h-8 rounded-full object-cover border border-gray-200 bg-white"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : null}
                    <span>{affiliateName}</span>
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={handlePrint}
                  disabled={isPrinting || !url}
                  className="mt-3 w-full px-4 py-3 rounded-xl bg-secondary-green text-white font-bold hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isPrinting ? 'Skriver ut...' : 'Skriv ut QR-kod'}
                </button>
              </>
            ) : affiliateName ? (
              <div className="mt-3 flex items-center gap-2.5 text-base text-gray-800 font-bold">
                {affiliateImage ? (
                  <img
                    src={affiliateImage}
                    alt={affiliateName}
                    className="w-8 h-8 rounded-full object-cover border border-gray-200 bg-white"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : null}
                <span>{affiliateName}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DishQrModal;

